import { useEffect } from 'react';

import { Alert, Stack } from '@mui/material';

import type { IMultiStepActions } from '@farmshare/ui-components';

import { useCustomerMergeCandidatesQuery } from '../../../lib/graphql';

import {
  blockedRetirees,
  masterPatch,
  type MergeWizardData,
  selectedIdsOf,
} from '../hooks/merge-wizard-data';
import { MergeWizardCompareStep } from './merge-wizard-compare-step';
import { candidateLabel, type WizardCandidate } from './merge-wizard-types';

export interface MergeWizardCompareModalStepProps {
  data: MergeWizardData;
  actions: IMultiStepActions<MergeWizardData>;
  /** The customer the wizard was opened from. */
  initiatingId: string;
  /** The processor the merge runs for. */
  vendorId: string | undefined;
}

/**
 * Step 2 of the merge wizard — choose the surviving record and, per field,
 * which value it keeps.
 *
 * Owns the candidate query and the two rules that pick a default primary: the
 * originating customer wins unless someone in the set has a login, in which
 * case a login-bearing record has to, since the survivor keeps its own Cognito
 * account.
 * @param root0 - Props.
 * @param root0.data - Current wizard data.
 * @param root0.actions - Multi-step actions.
 * @param root0.initiatingId - The customer the wizard was opened from.
 * @param root0.vendorId - The processor the merge runs for.
 * @returns The step body.
 */
export function MergeWizardCompareModalStep({
  data,
  actions,
  initiatingId,
  vendorId,
}: MergeWizardCompareModalStepProps) {
  const selectedIds = selectedIdsOf(data);
  const { updateData } = actions;

  const { data: candidatesData, loading } = useCustomerMergeCandidatesQuery({
    variables: { ids: selectedIds, vendorId },
    skip: selectedIds.length < 2,
    fetchPolicy: 'network-only',
  });

  const candidates = (candidatesData?.customerMergeCandidates ??
    []) as WizardCandidate[];

  // Apollo hands back a fresh array on every render, so the effects below key
  // off the ids rather than the array itself — otherwise each publish would
  // re-render, produce a new array, and publish again.
  const candidateKey = candidates.map((c) => c._id).join(',');
  const selectedKey = selectedIds.join(',');
  const { masterId } = data;

  // Publish the query into the shared bag so `canGoNext` can gate on it.
  useEffect(() => {
    updateData({ candidates, candidatesLoading: loading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateKey, loading, updateData]);

  // Default the primary to the originating customer once we have a set. A
  // primary that is no longer in the set counts as unset: the Select step
  // clears it on any change, and this is the same rule applied where the
  // default is chosen, so a stale id can never survive to the gate.
  useEffect(() => {
    if (selectedIds.length < 2) return;
    if (masterId && selectedIds.includes(masterId)) return;
    const next = selectedIds.includes(initiatingId)
      ? initiatingId
      : selectedIds[0];
    updateData(masterPatch(next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterId, selectedKey, initiatingId, updateData]);

  // When any record has a login, the primary must be a login-bearing record
  // (the survivor keeps its own Cognito account). If the default primary isn't,
  // switch to the first one that is.
  useEffect(() => {
    if (!masterId || candidates.length !== selectedIds.length) return;
    const anyHasLogin = candidates.some((c) => c.hasLogin);
    const master = candidates.find((c) => c._id === masterId);
    if (anyHasLogin && master && !master.hasLogin) {
      const loginRecord = candidates.find((c) => c.hasLogin);
      if (loginRecord) {
        updateData(masterPatch(loginRecord._id));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateKey, masterId, updateData]);

  // A record that also does business at another processor can be kept but not
  // retired; Next stays off until it's the primary or out of the merge.
  const blocked = blockedRetirees(data);

  return (
    <Stack spacing={2}>
      {blocked.map((c) => (
        <Alert key={c._id} severity="error">
          <strong>{candidateLabel(c)}</strong> can&apos;t be retired.{' '}
          {c.retireBlockedReason}
        </Alert>
      ))}
      <MergeWizardCompareStep
        candidates={candidates}
        masterId={masterId}
        onMasterChange={(id) => updateData(masterPatch(id))}
        fieldSources={data.fieldSources}
        onFieldSourceChange={(field, sourceId) =>
          updateData({
            fieldSources: { ...data.fieldSources, [field]: sourceId },
          })
        }
        showAll={data.showAll}
        onToggleShowAll={() => updateData({ showAll: !data.showAll })}
      />
    </Stack>
  );
}
